import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { TeamSnapshotWithUser } from '@/api/type-response'
import { message, Modal, Trans } from '@/components'
import { Snapshot } from '@/models/snapshot'

import { showShareToTeamModal } from '../components/ShareToTeamModal'
import { getSnapshotShareResultCode, SNAPSHOT_BILLING_MODE_CONFLICT } from '../utils/shareResult'

interface UseSnapshotOperationsOptions {
  teamResID?: string
  onSuccess?: () => void
}

export function useSnapshotOperations({ teamResID, onSuccess }: UseSnapshotOperationsOptions = {}) {
  const { t } = useTranslation()

  // Delete personal snapshot
  const deleteSnapshot = useCallback(
    (snap: Snapshot) => {
      return new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: t('snapshot.deleteModal.title'),
          content: (
            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
              <div className="t-sm text-secondary flex items-center">
                {t('snapshot.deleteModal.content')}
              </div>
            </div>
          ),
          confirmBtnCfg: {
            type: 'danger',
            text: t('snapshot.deleteModal.confirmBtn'),
          },
          className: 'w-[640px]',
        }).then(async () => {
          try {
            await API.deleteSnapshot({ res_id: snap.resID })
            message.success(t('snapshot.deleteModal.successMsg'))
            onSuccess?.()
            resolve()
          } catch (error) {
            console.error('Failed to delete snapshot:', error)
            reject(error)
          }
        })
      })
    },
    [t, onSuccess],
  )

  // Bulk delete personal snapshots
  const bulkDeleteSnapshots = useCallback(
    async (snapshots: Snapshot[]) => {
      const results = await Promise.allSettled(
        snapshots.map((snap) => API.deleteSnapshot({ res_id: snap.resID })),
      )

      const failed = results.filter((r) => r.status === 'rejected')
      if (failed.length > 0) {
        message.error(
          t('teamSnapshot.bulkDelete.partialError', {
            failed: failed.length,
            total: snapshots.length,
            defaultValue: `Failed to delete ${failed.length} of ${snapshots.length} snapshots`,
          }),
        )
      } else {
        message.success(
          t('teamSnapshot.bulkDelete.success', {
            count: snapshots.length,
            defaultValue: `Successfully deleted ${snapshots.length} snapshots`,
          }),
        )
      }

      onSuccess?.()
    },
    [t, onSuccess],
  )

  // Share personal snapshot to team
  const shareToTeam = useCallback(
    (snap: Snapshot, ownerName: string) => {
      if (!teamResID) {
        console.error('teamResID is required for shareToTeam')
        return
      }

      showShareToTeamModal({
        snapshotId: snap.resID,
        snapshotName: snap.name || snap.resID,
        ownerName,
        sizeInBytes: snap.size,
        teamResID,
        onSuccess: onSuccess || (() => {}),
      })
    },
    [teamResID, onSuccess],
  )

  // Share team member snapshot to shared pool
  const importToShared = useCallback(
    (item: TeamSnapshotWithUser, ownerName: string) => {
      if (!teamResID) {
        console.error('teamResID is required for importToShared')
        return
      }

      const sizeInBytes =
        typeof item.snapshot.sizeByte === 'string'
          ? parseInt(item.snapshot.sizeByte, 10)
          : item.snapshot.sizeByte

      showShareToTeamModal({
        snapshotId: item.snapshot.resID,
        snapshotName: item.snapshot.flag?.subject || item.snapshot.resID,
        ownerName,
        sizeInBytes,
        teamResID,
        onSuccess: onSuccess || (() => {}),
      })
    },
    [teamResID, onSuccess],
  )

  // Remove snapshot from team shared pool
  const removeFromShared = useCallback(
    (item: TeamSnapshotWithUser) => {
      if (!teamResID) {
        console.error('teamResID is required for removeFromShared')
        return Promise.reject(new Error('teamResID is required'))
      }

      return new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: t('teamSnapshot.shared.deleteModal.title', 'Delete Snapshot'),
          content: (
            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
              <div className="t-sm text-secondary">
                <Trans i18nKey="teamSnapshot.shared.removeModal.content">
                  This action will permanently delete this snapshot. Are you sure?
                </Trans>
              </div>
            </div>
          ),
          confirmBtnCfg: {
            type: 'danger',
            text: t('teamSnapshot.shared.deleteModal.confirmBtn', 'Remove'),
          },
          className: 'w-[640px]',
        }).then(async () => {
          try {
            await API.removeTeamSharedSnapshot({
              res_id: teamResID,
              snap_res_id: item.snapshot.resID,
            })
            message.success(
              t(
                'teamSnapshot.shared.deleteSuccess',
                'Snapshot removed from shared pool successfully',
              ),
            )
            onSuccess?.()
            resolve()
          } catch (error) {
            console.error('Failed to remove snapshot from shared:', error)
            message.error(t('teamSnapshot.shared.deleteError', 'Failed to remove snapshot'))
            reject(error)
          }
        })
      })
    },
    [teamResID, t, onSuccess],
  )

  // Bulk remove snapshots from shared pool
  const bulkRemoveFromShared = useCallback(
    (snapshotIds: string[]) => {
      if (!teamResID) {
        console.error('teamResID is required for bulkRemoveFromShared')
        return Promise.reject(new Error('teamResID is required'))
      }

      return new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: t('teamSnapshot.shared.bulkDeleteModal.title', 'Delete Snapshots'),
          content: (
            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
              <div className="t-sm text-secondary">
                <Trans
                  i18nKey="teamSnapshot.shared.bulkDeleteModal.content"
                  values={{ count: snapshotIds.length }}
                >
                  Are you sure you want to delete {snapshotIds.length} snapshot(s)? This action
                  cannot be undone.
                </Trans>
              </div>
            </div>
          ),
          confirmBtnCfg: {
            type: 'danger',
            text: t('teamSnapshot.shared.bulkDeleteModal.confirmBtn', 'Delete'),
          },
          className: 'w-[640px]',
        }).then(async () => {
          try {
            const results = await Promise.allSettled(
              snapshotIds.map((id) =>
                API.removeTeamSharedSnapshot({
                  res_id: teamResID,
                  snap_res_id: id,
                }),
              ),
            )

            const succeeded = results.filter((r) => r.status === 'fulfilled').length
            const failed = results.filter((r) => r.status === 'rejected').length

            if (failed === 0) {
              message.success(
                t('teamSnapshot.shared.bulkDeleteSuccess', {
                  count: succeeded,
                  defaultValue: `Successfully deleted ${succeeded} snapshot(s)`,
                }),
              )
            } else {
              message.error(
                t('teamSnapshot.shared.bulkDeletePartialError', {
                  succeeded,
                  failed,
                  defaultValue: `Deleted ${succeeded} snapshot(s), ${failed} failed`,
                }),
              )
            }

            onSuccess?.()
            resolve()
          } catch (error) {
            console.error('Failed to bulk delete snapshots:', error)
            message.error(t('teamSnapshot.shared.bulkDeleteError', 'Failed to delete snapshots'))
            reject(error)
          }
        })
      })
    },
    [teamResID, t, onSuccess],
  )

  // Bulk share team member snapshots to shared pool
  const bulkImportToShared = useCallback(
    (snapshots: TeamSnapshotWithUser[]) => {
      if (!teamResID) {
        console.error('teamResID is required for bulkImportToShared')
        return Promise.reject(new Error('teamResID is required'))
      }

      const snapshotIds = snapshots.map((s) => s.snapshot.resID)

      return new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: t('teamSnapshot.bulkShare.title', 'Share Selected Snapshots to Team'),
          content: (
            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
              <div className="t-sm text-secondary flex items-center">
                <Trans
                  i18nKey="teamSnapshot.bulkShare.content"
                  values={{ count: snapshotIds.length }}
                >
                  Are you sure you want to share {snapshotIds.length} snapshots to the team shared
                  space?
                </Trans>
              </div>
            </div>
          ),
          confirmBtnCfg: {
            type: 'secondary',
            text: t('teamSnapshot.bulkShare.confirmBtn', 'Share'),
          },
          className: 'w-[640px]',
        }).then(async () => {
          try {
            const results = await Promise.allSettled(
              snapshots.map((snap) =>
                API.importTeamSnapshot(
                  {
                    res_id: teamResID,
                    snap_res_id: snap.snapshot.resID,
                  },
                  { errMsg: false, noErrMsg: true },
                ),
              ),
            )

            // Check both rejected promises and fulfilled promises with non-zero code
            const failed = results.filter(
              (r) => r.status === 'rejected' || getSnapshotShareResultCode(r.value) !== 0,
            )
            const billingConflicts = failed.filter(
              (r) =>
                getSnapshotShareResultCode(r.status === 'fulfilled' ? r.value : r.reason) ===
                SNAPSHOT_BILLING_MODE_CONFLICT,
            ).length

            if (failed.length === 0) {
              message.success(
                t('teamSnapshot.bulkShare.success', {
                  count: snapshotIds.length,
                  defaultValue: `Successfully shared ${snapshotIds.length} snapshots`,
                }),
              )
            } else {
              const summary = t('teamSnapshot.bulkShare.partialError', {
                succeeded: snapshotIds.length - failed.length,
                failed: failed.length,
                total: snapshotIds.length,
                defaultValue: 'Shared {{succeeded}} snapshots; {{failed}} of {{total}} failed.',
              })
              const conflictReason =
                billingConflicts > 0
                  ? t('teamSnapshot.bulkShare.billingModeConflict', {
                      count: billingConflicts,
                      defaultValue:
                        "Snapshots with a billing mode conflict: {{count}}. Their owners' snapshot billing modes differ from the team's shared storage billing mode.",
                    })
                  : ''
              message.error([summary, conflictReason].filter(Boolean).join(' '))
            }

            onSuccess?.()
            resolve()
          } catch (error) {
            console.error('Failed to bulk share snapshots:', error)
            reject(error)
          }
        })
      })
    },
    [teamResID, t, onSuccess],
  )

  return {
    deleteSnapshot,
    bulkDeleteSnapshots,
    shareToTeam,
    importToShared,
    removeFromShared,
    bulkRemoveFromShared,
    bulkImportToShared,
  }
}
